import type { PipelinesClient } from "@databricks/sdk-pipelines/v2";
import type { ToolDef } from "@fabric-harness/sdk";
import {
  assertExactResourceAllowed,
  pinnedStringResourceSchema,
  resolveExactResourcePolicy,
} from "./resource-policy.js";

export type DatabricksPipelinesClient = Pick<PipelinesClient, "list" | "get" | "start" | "stop">;
type ToolMeta = { name?: string; description?: string };
export type DatabricksPipelineRunPolicy =
  | { allowedPipelineIds: readonly string[]; allowAnyPipelineId?: never }
  | { allowedPipelineIds?: never; allowAnyPipelineId: true };

/**
 * Lakeflow Declarative Pipelines (formerly DLT). These drive heavy continuous-compute consumption.
 * `start`/`stop` mutate pipeline state (`effect: 'execute'`, governable); `list`/`status` are reads.
 */

export function databricksPipelineListTool(
  client: DatabricksPipelinesClient,
  options: ToolMeta = {},
): ToolDef<{ maxResults?: number; filter?: string }, unknown> {
  return {
    name: options.name ?? "databricks_pipeline_list",
    description:
      options.description ?? "List Lakeflow Declarative Pipelines (DLT) in the workspace.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { maxResults: { type: "number", minimum: 1 }, filter: { type: "string" } },
    },
    metadata: { effect: "read", provider: "databricks", service: "lakeflow" },
    execute: (input) =>
      client.list({
        ...(input.maxResults !== undefined ? { maxResults: input.maxResults } : {}),
        ...(input.filter ? { filter: input.filter } : {}),
      }),
  };
}

export function databricksPipelineStatusTool(
  client: DatabricksPipelinesClient,
  options: ToolMeta = {},
): ToolDef<{ pipelineId: string }, unknown> {
  return {
    name: options.name ?? "databricks_pipeline_status",
    description:
      options.description ?? "Get a Lakeflow pipeline, including its state and latest updates.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { pipelineId: { type: "string" } },
      required: ["pipelineId"],
    },
    metadata: { effect: "read", provider: "databricks", service: "lakeflow" },
    execute: (input) => client.get({ pipelineId: input.pipelineId }),
  };
}

export function databricksPipelineStartTool(
  client: DatabricksPipelinesClient,
  runPolicy: DatabricksPipelineRunPolicy,
  options: ToolMeta = {},
): ToolDef<{ pipelineId: string; fullRefresh?: boolean }, unknown> {
  const allowedPipelineIds = resolveExactResourcePolicy(
    runPolicy,
    "allowedPipelineIds",
    "allowAnyPipelineId",
    "Lakeflow pipeline",
  );
  return {
    name: options.name ?? "databricks_pipeline_start",
    description:
      options.description ??
      "Trigger a Lakeflow pipeline update (run). Set fullRefresh to reprocess all data.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        pipelineId: pinnedStringResourceSchema(allowedPipelineIds),
        fullRefresh: { type: "boolean" },
      },
      required: ["pipelineId"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "lakeflow",
      governance: { resources: [{ kind: "pipeline", path: "/pipelineId" }] },
    },
    async execute(input) {
      assertExactResourceAllowed("Lakeflow pipeline", input.pipelineId, allowedPipelineIds);
      return await client.start({
        pipelineId: input.pipelineId,
        ...(input.fullRefresh !== undefined ? { fullRefresh: input.fullRefresh } : {}),
      });
    },
  };
}

export function databricksPipelineStopTool(
  client: DatabricksPipelinesClient,
  runPolicy: DatabricksPipelineRunPolicy,
  options: ToolMeta = {},
): ToolDef<{ pipelineId: string }, unknown> {
  const allowedPipelineIds = resolveExactResourcePolicy(
    runPolicy,
    "allowedPipelineIds",
    "allowAnyPipelineId",
    "Lakeflow pipeline",
  );
  return {
    name: options.name ?? "databricks_pipeline_stop",
    description: options.description ?? "Stop the active update of a Lakeflow pipeline.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { pipelineId: pinnedStringResourceSchema(allowedPipelineIds) },
      required: ["pipelineId"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "lakeflow",
      governance: { resources: [{ kind: "pipeline", path: "/pipelineId" }] },
    },
    async execute(input) {
      assertExactResourceAllowed("Lakeflow pipeline", input.pipelineId, allowedPipelineIds);
      return await client.stop({ pipelineId: input.pipelineId });
    },
  };
}

/** The default Lakeflow tool set. Omitting runPolicy is intentionally read-only. */
export function databricksLakeflowTools(
  client: DatabricksPipelinesClient,
  runPolicy?: DatabricksPipelineRunPolicy,
): ToolDef[] {
  return [
    databricksPipelineListTool(client) as ToolDef,
    databricksPipelineStatusTool(client) as ToolDef,
    ...(runPolicy
      ? [
          databricksPipelineStartTool(client, runPolicy) as ToolDef,
          databricksPipelineStopTool(client, runPolicy) as ToolDef,
        ]
      : []),
  ];
}
